import { useState } from 'react';
import { Cpu, HardDrive, MemoryStick, Plug } from 'lucide-react';
import type { Resource } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useFetch, useToast } from './ui';
import { TimeseriesChart } from './charts';

type Raw = Array<{ points: Array<[number, number | null]>; labels: Record<string, string> }>;

export const VITAL_METRICS = ['cpu.usage', 'memory.usage', 'disk.usage'] as const;

const CARDS = [
  { metric: 'cpu.usage', label: 'CPU', icon: Cpu },
  { metric: 'memory.usage', label: 'Memory', icon: MemoryStick },
  { metric: 'disk.usage', label: 'Disk', icon: HardDrive },
] as const;

function last(points: Array<[number, number | null]>): number | null {
  for (let i = points.length - 1; i >= 0; i--) {
    const v = points[i]?.[1];
    if (v !== null && v !== undefined) return v;
  }
  return null;
}

function VitalCard({ resourceId, metric, label, icon: Icon, hours }: { resourceId: string; metric: string; label: string; icon: typeof Cpu; hours: number }) {
  const { data } = useFetch(
    () => {
      const to = Date.now();
      return api.get<Raw>('/api/metrics/query', {
        resourceId,
        metric,
        from: to - hours * 3_600_000,
        to,
        stepSec: Math.max(60, Math.floor((hours * 3600) / 120)),
        agg: 'avg',
      });
    },
    [resourceId, metric, hours],
    { refreshMs: 30_000 },
  );

  const series = (data ?? []).map((s) => ({ name: Object.values(s.labels).join(' ') || label, points: s.points }));
  // Disk is one series per mount: the card headline is the fullest one.
  const current = series.reduce<number | null>((m, s) => {
    const v = last(s.points);
    return v !== null && (m === null || v > m) ? v : m;
  }, null);
  const color = current === null ? undefined : current >= 90 ? 'var(--itr-danger)' : current >= 75 ? 'var(--itr-warning)' : 'var(--itr-success)';

  return (
    <div className="card">
      <div className="card-header">
        <Icon size={14} />
        <span className="card-title">{label}</span>
        <span className="grow" />
        <span className="nums" style={{ fontSize: 20, fontWeight: 600, color }}>
          {current === null ? '—' : `${current.toFixed(0)}%`}
        </span>
      </div>
      <div className="card-body">
        <div style={{ height: 6, borderRadius: 3, background: 'var(--itr-border)', overflow: 'hidden', marginBottom: 10 }}>
          <div style={{ width: `${Math.min(100, current ?? 0)}%`, height: '100%', background: color }} />
        </div>
        <div style={{ height: 120 }}>
          {series.length > 0 ? (
            <TimeseriesChart series={series} unit="percent" height={120} />
          ) : (
            <div className="subtle" style={{ fontSize: 12, paddingTop: 20 }}>No samples in this window.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function InstallSnmp({ resource, onDone }: { resource: Resource; onDone: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [username, setUsername] = useState(String(resource.config.sshUsername ?? resource.config.username ?? 'root'));
  const [password, setPassword] = useState('');
  const [output, setOutput] = useState<string | null>(null);
  if (!can('tools:provision')) return null;

  const hasSavedLogin = Boolean(resource.config.sshPassword || resource.config.password || resource.config.sshPrivateKey || resource.config.privateKey);

  const run = async () => {
    setBusy(true);
    setOutput(null);
    try {
      const r = await api.post<{ ok: boolean; verified: boolean; output: string }>(`/api/tools/resource/${resource.id}/install-snmp`, password ? { username, password } : {});
      if (r.ok) {
        toast(r.verified ? 'SNMP installed and answering' : 'SNMP installed — not answering yet, check the firewall', r.verified ? 'good' : 'warn');
        setOpen(false);
        onDone();
      } else {
        setOutput(r.output || 'The install script did not finish.');
      }
    } catch (err) {
      setOutput(err instanceof Error ? err.message : 'Install failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button className="btn btn-sm" onClick={() => setOpen(true)}>
        <Plug size={12} /> Install SNMP on this server
      </button>
    );
  }
  return (
    <div className="card" style={{ padding: 12, marginTop: 10, maxWidth: 520 }}>
      <div style={{ fontSize: 12.5, marginBottom: 8 }}>
        Logs in over SSH, installs <b>snmpd</b>, and allows read-only SNMP from this monitoring server only. A random community is generated and saved on the resource.
      </div>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="SSH user" style={{ width: 150 }} />
        <input
          className="input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={hasSavedLogin ? 'Password (blank = saved login)' : 'SSH / sudo password'}
          style={{ width: 230 }}
        />
        <button className="btn btn-sm btn-primary" onClick={run} disabled={busy || (!password && !hasSavedLogin)}>
          {busy ? <span className="spinner" style={{ width: 12, height: 12 }} /> : null} Install
        </button>
        <button className="btn btn-sm" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
      </div>
      {output && <pre className="mono" style={{ fontSize: 11, marginTop: 8, whiteSpace: 'pre-wrap', color: 'var(--itr-critical)' }}>{output}</pre>}
    </div>
  );
}

/** CPU / memory / disk cards with graphs, shown first on every resource page. */
export function SystemResources({ resource, metrics, hours, onChanged }: { resource: Resource; metrics: string[]; hours: number; onChanged: () => void }) {
  const resourceId = resource.id;
  const present = CARDS.filter((c) => metrics.includes(c.metric));
  if (present.length === 0) {
    const via = resource.attributes.vitalsVia as string | null | undefined;
    const error = resource.attributes.vitalsError as string | null | undefined;
    return (
      <div className="card" style={{ padding: 14, marginBottom: 14 }}>
        <div className="subtle" style={{ fontSize: 12.5 }}>
          No CPU, memory or disk data yet.{' '}
          {error
            ? `The last attempt over SNMP/SSH failed — ${error}`
            : via
              ? `Reading over ${via.toUpperCase()}; the first values appear after the next poll.`
              : 'Set an SSH login or an SNMP community on this resource and it is read automatically, or install SNMP below.'}
        </div>
        <InstallSnmp resource={resource} onDone={onChanged} />
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 14 }}>
      {present.map((c) => (
        <VitalCard key={c.metric} resourceId={resourceId} hours={hours} {...c} />
      ))}
    </div>
  );
}
